import React, { useState, useEffect } from 'react';
import { getEventTicketTypes } from '../services/eventTicketService';
import './EventTicketSelector.css';

const EventTicketSelector = ({
  event,
  onSelectionChange,
  onBack,
  restaurantName
}) => {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selections, setSelections] = useState({});

  useEffect(() => {
    const fetchTicketTypes = async () => {
      if (!event?.id) return;

      setLoading(true);
      setError('');
      try {
        const response = await getEventTicketTypes(event.id);
        // Handle both array and { data: [...] } response formats
        const types = Array.isArray(response) ? response : (response.data || []);
        setTicketTypes(types);

        // Initialize selections with 0 for each type
        const initialSelections = {};
        types.forEach(type => {
          initialSelections[type.id] = 0;
        });
        setSelections(initialSelections);
      } catch (err) {
        console.error('Error fetching ticket types:', err);
        setError('Failed to load ticket types. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTicketTypes();
  }, [event?.id]);

  const handleQuantityChange = (typeId, delta) => {
    const ticketType = ticketTypes.find(t => t.id === typeId);
    if (!ticketType) return;

    const currentQty = selections[typeId] || 0;
    const newQty = Math.max(0, currentQty + delta);

    // Check max per order (default 10 if not set)
    const maxPerOrder = ticketType.maxPerOrder || 10;
    if (newQty > maxPerOrder) return;

    // Check remaining quantity
    if (ticketType.remainingQuantity != null && newQty > ticketType.remainingQuantity) return;

    const newSelections = { ...selections, [typeId]: newQty };
    setSelections(newSelections);

    // Notify parent of selection change
    if (onSelectionChange) {
      const selectedTickets = ticketTypes
        .filter(t => newSelections[t.id] > 0)
        .map(t => ({
          ticketTypeId: t.id,
          ticketTypeName: t.name,
          quantity: newSelections[t.id],
          priceCents: t.priceCents,
          priceExcludesVat: t.priceExcludesVat
        }));

      onSelectionChange(selectedTickets, calculateTotal(newSelections));
    }
  };

  const calculateTotal = (currentSelections = selections) => {
    let total = 0;
    ticketTypes.forEach(type => {
      const qty = currentSelections[type.id] || 0;
      const basePrice = type.priceCents / 100;
      const priceWithVat = type.priceExcludesVat ? basePrice * 1.14 : basePrice;
      total += qty * priceWithVat;
    });
    return total;
  };

  const getTotalTickets = () => {
    return Object.values(selections).reduce((sum, qty) => sum + qty, 0);
  };

  const formatPrice = (cents, excludesVat = false) => {
    const price = cents / 100;
    if (excludesVat) {
      return `EGP ${price.toFixed(0)} + VAT`;
    }
    return `EGP ${price.toFixed(0)}`;
  };

  const formatDateTime = (dateStr, startTime, endTime) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateFormatted = date.toLocaleDateString('en-US', options);

    const formatTime = (time) => {
      if (!time) return '';
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    };

    let timeStr = '';
    if (startTime) {
      timeStr = formatTime(startTime);
      if (endTime) {
        timeStr += ` - ${formatTime(endTime)}`;
      }
    }

    return { dateFormatted, timeStr };
  };

  if (loading) {
    return (
      <div className="ticket-selector-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading ticket options...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticket-selector-error">
        <i className="bi bi-exclamation-circle"></i>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Try Again
        </button>
      </div>
    );
  }

  const { dateFormatted, timeStr } = formatDateTime(event.date, event.startTime, event.endTime);
  const totalPrice = calculateTotal();
  const totalTickets = getTotalTickets();

  return (
    <div className="event-ticket-selector">
      {/* Event Header */}
      <div className="event-header">
        {event.imageUrl && (
          <div className="event-image">
            <img src={event.imageUrl} alt={event.name} />
          </div>
        )}
        <div className="event-info">
          <h3 className="event-title">{event.name}</h3>
          <p className="event-venue">
            <i className="bi bi-geo-alt-fill"></i>
            {restaurantName}
          </p>
          <p className="event-datetime">
            <i className="bi bi-calendar-event"></i>
            {dateFormatted}
            {timeStr && <span className="event-time"> at {timeStr}</span>}
          </p>
          {event.description && (
            <p className="event-description">{event.description}</p>
          )}
        </div>
      </div>

      {/* Ticket Types */}
      <div className="ticket-types-section">
        <h4 className="section-title">
          <i className="bi bi-ticket-perforated"></i>
          Select Your Tickets
        </h4>

        {ticketTypes.length === 0 ? (
          <div className="no-tickets-available">
            <i className="bi bi-ticket-detailed"></i>
            <p>No tickets available for this event.</p>
          </div>
        ) : (
          <div className="ticket-types-list">
            {ticketTypes.map(type => {
              const currentQty = selections[type.id] || 0;
              const isSoldOut = type.remainingQuantity != null && type.remainingQuantity === 0;
              const maxPerOrder = type.maxPerOrder || 10;
              const isMaxReached = currentQty >= maxPerOrder ||
                (type.remainingQuantity != null && currentQty >= type.remainingQuantity);
              const isMinReached = currentQty <= 0;

              return (
                <div
                  key={type.id}
                  className={`ticket-type-card ${isSoldOut ? 'sold-out' : ''} ${currentQty > 0 ? 'selected' : ''}`}
                >
                  <div className="ticket-type-info">
                    <div className="ticket-type-header">
                      <h5 className="ticket-type-name">{type.name}</h5>
                      <span className="ticket-type-price">
                        {formatPrice(type.priceCents, type.priceExcludesVat)}
                      </span>
                    </div>
                    {type.description && (
                      <p className="ticket-type-description">{type.description}</p>
                    )}
                    <div className="ticket-type-meta">
                      {isSoldOut && (
                        <span className="sold-out-badge">
                          <i className="bi bi-x-circle"></i>
                          Sold Out
                        </span>
                      )}
                      {maxPerOrder && (
                        <span className="max-per-order-info">
                          Max {maxPerOrder} per order
                        </span>
                      )}
                    </div>
                  </div>

                  {!isSoldOut && (
                    <div className="ticket-quantity-controls">
                      <button
                        className="qty-btn minus"
                        onClick={() => handleQuantityChange(type.id, -1)}
                        disabled={isMinReached}
                        aria-label="Decrease quantity"
                      >
                        <i className="bi bi-dash"></i>
                      </button>
                      <span className="qty-value">{currentQty}</span>
                      <button
                        className="qty-btn plus"
                        onClick={() => handleQuantityChange(type.id, 1)}
                        disabled={isMaxReached}
                        aria-label="Increase quantity"
                      >
                        <i className="bi bi-plus"></i>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Summary */}
      {totalTickets > 0 && (
        <div className="order-summary">
          <div className="summary-row">
            <span className="summary-label">Total Tickets:</span>
            <span className="summary-value">{totalTickets}</span>
          </div>
          <div className="summary-row total">
            <span className="summary-label">Total Amount:</span>
            <span className="summary-value">EGP {totalPrice.toFixed(2)}</span>
          </div>
          {ticketTypes.some(t => t.priceExcludesVat && selections[t.id] > 0) && (
            <p className="vat-note">
              <i className="bi bi-info-circle"></i>
              Price includes 14% VAT
            </p>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="ticket-selector-actions">
        {onBack && (
          <button className="btn btn-back" onClick={onBack}>
            <i className="bi bi-arrow-left"></i>
            Back
          </button>
        )}
      </div>
    </div>
  );
};

export default EventTicketSelector;
